/**
 * Excel utility functions for parsing and creating Excel files.
 * Supports multi-sheet workbooks, text column preservation (for leading zeros),
 * and various export formats.
 *
 * NOTE: These utilities require the 'xlsx' package as a peer dependency.
 * Install it in your app: npm install xlsx
 */

/**
 * Helper to get XLSX library - tries dynamic import first, falls back to require-style
 * @returns {Promise<Object>} XLSX library
 */
const getXLSX = async () => {
  // Try dynamic import for apps with ES modules
  try {
    return await import('xlsx');
  } catch {
    // If dynamic import fails, the app should have xlsx installed
    throw new Error(
      'xlsx library not available. Please install it: npm install xlsx'
    );
  }
};

/**
 * Parses a multi-sheet Excel file and returns all sheets' data.
 *
 * @param {File} file - The Excel file to parse
 * @param {Object} options - Parsing options
 * @param {boolean} [options.raw=false] - If true, returns raw cell values without formatting
 * @param {*} [options.defval=''] - Default value for empty cells
 * @returns {Promise<Object>} Object containing workbook, sheetsData, and sheetNames
 *
 * @example
 * const { workbook, sheetsData, sheetNames } = await parseExcelWorkbook(file);
 * const sheet1Data = sheetsData[sheetNames[0]]; // 2D array of first sheet
 */
export const parseExcelWorkbook = async (file, options = {}) => {
  const XLSX = await getXLSX();

  const { raw = false, defval = '' } = options;

  const arrayBuffer = await file.arrayBuffer();
  const workbook = XLSX.read(arrayBuffer, { type: 'array', cellDates: true });

  const sheetsData = {};
  workbook.SheetNames.forEach((sheetName) => {
    sheetsData[sheetName] = XLSX.utils.sheet_to_json(workbook.Sheets[sheetName], {
      header: 1,
      defval,
      raw,
    });
  });

  return {
    workbook,
    sheetsData,
    sheetNames: workbook.SheetNames,
  };
};

/**
 * Parses a single Excel sheet to JSON objects with headers as keys.
 *
 * @param {File} file - The Excel file to parse
 * @param {Object} options - Parsing options
 * @param {string} [options.sheetName] - Specific sheet to parse (default: first sheet)
 * @param {number} [options.headerRow=0] - Row index containing headers (0-based)
 * @param {number} [options.dataStartRow=1] - Row index where data starts (0-based)
 * @param {boolean} [options.skipEmptyRows=true] - Skip rows where all values are empty
 * @returns {Promise<Array<Object>>} Array of row objects
 */
export const parseExcelToJson = async (file, options = {}) => {
  const XLSX = await getXLSX();

  const {
    sheetName,
    headerRow = 0,
    dataStartRow = 1,
    skipEmptyRows = true,
  } = options;

  const arrayBuffer = await file.arrayBuffer();
  const workbook = XLSX.read(arrayBuffer, { type: 'array', cellDates: true });

  const targetSheet = sheetName || workbook.SheetNames[0];
  const sheet = workbook.Sheets[targetSheet];

  if (!sheet) {
    throw new Error(`Sheet "${targetSheet}" not found in workbook`);
  }

  // Get raw 2D array
  const rawData = XLSX.utils.sheet_to_json(sheet, {
    header: 1,
    defval: '',
    raw: false,
  });

  if (rawData.length === 0) {
    return [];
  }

  // Extract headers
  const headers = rawData[headerRow] || [];

  // Process data rows
  const result = [];
  for (let i = dataStartRow; i < rawData.length; i++) {
    const row = rawData[i] || [];

    // Check if row is empty
    if (skipEmptyRows) {
      const isEmptyRow = row.every(
        (cell) => cell === '' || cell === null || cell === undefined
      );
      if (isEmptyRow) continue;
    }

    // Create object from row
    const rowObj = {};
    headers.forEach((header, colIndex) => {
      if (header) {
        rowObj[header] = row[colIndex] !== undefined ? row[colIndex] : '';
      }
    });

    result.push(rowObj);
  }

  return result;
};

/**
 * Creates an Excel workbook with specified columns formatted as text.
 * This preserves leading zeros and prevents Excel from auto-converting values.
 *
 * @param {Object} options - Export options
 * @param {Array<Object>} options.data - Array of row objects to export
 * @param {Array<Object>} options.columns - Column definitions [{field, headerName}]
 * @param {Array<string>} [options.textColumns=[]] - Column fields to format as text
 * @param {string} [options.sheetName='Data'] - Name of the sheet
 * @param {string} [options.fileName='export.xlsx'] - Output file name
 * @returns {Promise<Blob>} Excel file as Blob
 *
 * @example
 * const blob = await createExcelWithTextColumns({
 *   data: rowData,
 *   columns: columnDefs,
 *   textColumns: ['CUSIP', 'ID2'], // Preserve leading zeros
 *   fileName: 'securities.xlsx'
 * });
 */
export const createExcelWithTextColumns = async (options) => {
  const XLSX = await getXLSX();

  const {
    data,
    columns,
    textColumns = [],
    sheetName = 'Data',
  } = options;

  // Create header row
  const headers = columns.map((col) => col.headerName || col.field);

  // Create data rows with text formatting for specified columns
  const rows = data.map((row) => {
    return columns.map((col) => {
      const value = row[col.field];

      // Format as text if specified (preserves leading zeros)
      if (textColumns.includes(col.field) && value !== null && value !== undefined) {
        return String(value);
      }

      return value;
    });
  });

  // Create worksheet from array of arrays
  const wsData = [headers, ...rows];
  const worksheet = XLSX.utils.aoa_to_sheet(wsData);

  // Set text columns to 's' (string) type to prevent number conversion
  if (textColumns.length > 0) {
    const textColIndices = columns
      .map((col, idx) => (textColumns.includes(col.field) ? idx : -1))
      .filter((idx) => idx !== -1);

    const range = XLSX.utils.decode_range(worksheet['!ref'] || 'A1');

    for (let row = 1; row <= range.e.r; row++) {
      textColIndices.forEach((colIdx) => {
        const cellAddress = XLSX.utils.encode_cell({ r: row, c: colIdx });
        if (worksheet[cellAddress]) {
          worksheet[cellAddress].t = 's'; // Set type to string
          worksheet[cellAddress].z = '@'; // Set format to text
        }
      });
    }
  }

  // Create workbook
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

  // Write to buffer
  const buffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });

  return new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
};

/**
 * Downloads an Excel buffer/blob as a file.
 *
 * @param {Blob|ArrayBuffer} buffer - The Excel data
 * @param {string} fileName - The file name for download
 */
export const downloadExcel = (buffer, fileName) => {
  const blob =
    buffer instanceof Blob
      ? buffer
      : new Blob([buffer], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });

  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * Creates and downloads an Excel file in one step.
 *
 * @param {Object} options - Same options as createExcelWithTextColumns
 */
export const exportToExcel = async (options) => {
  const { fileName = 'export.xlsx', ...restOptions } = options;
  const blob = await createExcelWithTextColumns(restOptions);
  downloadExcel(blob, fileName);
};

/**
 * Creates a template Excel file with headers only.
 *
 * @param {Array<Object>} columns - Column definitions [{field, headerName}]
 * @param {string} [fileName='template.xlsx'] - Output file name
 * @param {string} [sheetName='Template'] - Name of the sheet
 */
export const downloadTemplate = async (columns, fileName = 'template.xlsx', sheetName = 'Template') => {
  const XLSX = await getXLSX();

  const headers = columns.map((col) => col.headerName || col.field);
  const worksheet = XLSX.utils.aoa_to_sheet([headers]);

  // Set column widths based on header length
  worksheet['!cols'] = headers.map((header) => ({
    wch: Math.max(header.length + 2, 12),
  }));

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

  const buffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
  downloadExcel(buffer, fileName);
};

/**
 * Creates a multi-sheet Excel workbook.
 *
 * @param {Object} options - Export options
 * @param {Array<Object>} options.sheets - Array of sheet definitions
 * @param {string} options.sheets[].name - Sheet name
 * @param {Array<Object>} options.sheets[].data - Array of row objects
 * @param {Array<Object>} options.sheets[].columns - Column definitions
 * @param {Array<string>} [options.sheets[].textColumns] - Columns to format as text
 * @param {string} [options.fileName='export.xlsx'] - Output file name
 * @returns {Promise<Blob>} Excel file as Blob
 */
export const createMultiSheetExcel = async (options) => {
  const XLSX = await getXLSX();

  const { sheets } = options;
  const workbook = XLSX.utils.book_new();

  for (const sheet of sheets) {
    const { name, data, columns, textColumns = [] } = sheet;

    // Create header row
    const headers = columns.map((col) => col.headerName || col.field);

    // Create data rows
    const rows = data.map((row) =>
      columns.map((col) => {
        const value = row[col.field];
        if (textColumns.includes(col.field) && value !== null && value !== undefined) {
          return String(value);
        }
        return value;
      })
    );

    const wsData = [headers, ...rows];
    const worksheet = XLSX.utils.aoa_to_sheet(wsData);

    // Format text columns
    if (textColumns.length > 0) {
      const textColIndices = columns
        .map((col, idx) => (textColumns.includes(col.field) ? idx : -1))
        .filter((idx) => idx !== -1);

      const range = XLSX.utils.decode_range(worksheet['!ref'] || 'A1');

      for (let row = 1; row <= range.e.r; row++) {
        textColIndices.forEach((colIdx) => {
          const cellAddress = XLSX.utils.encode_cell({ r: row, c: colIdx });
          if (worksheet[cellAddress]) {
            worksheet[cellAddress].t = 's';
            worksheet[cellAddress].z = '@';
          }
        });
      }
    }

    XLSX.utils.book_append_sheet(workbook, worksheet, name);
  }

  const buffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
  return new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
};

/**
 * Reads CSV data from a file.
 *
 * @param {File} file - The CSV file to parse
 * @param {Object} options - Parsing options
 * @param {string} [options.delimiter=','] - CSV delimiter
 * @returns {Promise<Array<Array<string>>>} 2D array of CSV data
 */
export const parseCSV = async (file, options = {}) => {
  const XLSX = await getXLSX();

  const { delimiter = ',' } = options;

  const text = await file.text();
  const workbook = XLSX.read(text, {
    type: 'string',
    FS: delimiter,
  });

  const sheetName = workbook.SheetNames[0];
  const sheet = workbook.Sheets[sheetName];

  return XLSX.utils.sheet_to_json(sheet, {
    header: 1,
    defval: '',
    raw: false,
  });
};

/**
 * Detects if a file is Excel or CSV based on extension.
 *
 * @param {File} file - The file to check
 * @returns {string} 'excel' | 'csv' | 'unknown'
 */
export const detectFileType = (file) => {
  const name = file.name.toLowerCase();
  if (name.endsWith('.xlsx') || name.endsWith('.xls')) {
    return 'excel';
  }
  if (name.endsWith('.csv')) {
    return 'csv';
  }
  return 'unknown';
};

/**
 * Parses either Excel or CSV file based on file type.
 *
 * @param {File} file - The file to parse
 * @param {Object} options - Parsing options
 * @returns {Promise<Array<Array<string>>>} 2D array of data
 */
export const parseSpreadsheetFile = async (file, options = {}) => {
  const fileType = detectFileType(file);

  if (fileType === 'csv') {
    return parseCSV(file, options);
  }

  const { sheetsData, sheetNames } = await parseExcelWorkbook(file, options);
  return sheetsData[sheetNames[0]] || [];
};

export default {
  parseExcelWorkbook,
  parseExcelToJson,
  createExcelWithTextColumns,
  downloadExcel,
  exportToExcel,
  downloadTemplate,
  createMultiSheetExcel,
  parseCSV,
  detectFileType,
  parseSpreadsheetFile,
};
